"use client";

import { useAssetLibrary } from "@saas/shared/assets/useAssetLibrary";
import { cn } from "@ui/lib";
import { ChevronRightIcon, FolderIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import {
	type KeyboardEvent,
	type MouseEvent,
	type Ref,
	type RefObject,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { formatImageRef } from "../lib/image-refs";

export interface ImageRefPickerHandle {
	/** 弹层开着时接管输入框里的方向键 / 回车 / Esc；处理了返回 true */
	handleKeyDown: (event: KeyboardEvent) => boolean;
}

type PickerTab = "recent" | "favorite";

interface Tile {
	key: string;
	url: string;
	/** 已在上传区里的第几张；不在上传区为 null */
	n: number | null;
	disabled: boolean;
	title?: string;
}

const COLUMNS = 4;
/** Recent 里除了上传区的图，再放几张素材库的 */
const RECENT_ASSET_COUNT = 8;
/** 弹层和 Prompt 框、屏幕边缘的间距 */
const GAP = 8;

/**
 * Prompt 框 @ Reference 弹出的 My Assets 小面板（2026-10-21，照 DeeVid 的 Recent + Browse all）：
 * 上传区里的图排最前、标「Image N」，后面是素材库最近的图（上传的、生成的都有）。
 * 点上传区的图只插编号；点别的图先加进上传区当 Image N+1 再插编号。
 * 没登录只列上传区的图，不放登录入口（老公 10/21 定：DeeVid 没登录时 @ 什么都不弹，登录框太绕）。
 */
export function ImageRefPicker({
	open,
	onClose,
	inputImages,
	maxImages,
	isLoggedIn,
	onPickExisting,
	onPickAsset,
	onBrowseAll,
	ignoreRef,
	anchorRef,
	handleRef,
}: {
	open: boolean;
	onClose: () => void;
	inputImages: string[];
	maxImages: number;
	isLoggedIn: boolean;
	onPickExisting: (n: number) => void;
	onPickAsset: (url: string, from: PickerTab) => void;
	onBrowseAll: () => void;
	/** @ 按钮：点它不算「点外面」，否则会先关再被按钮重新打开 */
	ignoreRef: RefObject<HTMLElement | null>;
	/** Prompt 框：弹层和它同宽，优先贴在它上方 */
	anchorRef: RefObject<HTMLElement | null>;
	handleRef?: Ref<ImageRefPickerHandle>;
}) {
	const t = useTranslations("aiImageGenerator.client.imageRefs");
	const [tab, setTab] = useState<PickerTab>("recent");
	const [activeIndex, setActiveIndex] = useState(0);
	const panelRef = useRef<HTMLElement>(null);
	const [position, setPosition] = useState<{
		left: number;
		top: number;
		width: number;
		maxHeight: number;
	} | null>(null);
	const { items, isLoading } = useAssetLibrary({
		filter: tab === "favorite" ? "favorite" : "all",
		enabled: open && isLoggedIn,
	});

	const full = inputImages.length >= maxImages;
	const existing: Tile[] = inputImages.map((url, index) => ({
		key: `in-${url}`,
		url,
		n: index + 1,
		disabled: false,
	}));
	const assetTiles: Tile[] = items
		.filter((item) => tab === "favorite" || !inputImages.includes(item.url))
		.slice(0, tab === "favorite" ? 12 : RECENT_ASSET_COUNT)
		.map((item) => {
			const index = inputImages.indexOf(item.url);
			return {
				key: item.key,
				url: item.url,
				n: index >= 0 ? index + 1 : null,
				disabled: index < 0 && full,
				title: item.prompt,
			};
		});
	const tiles =
		!isLoggedIn || tab === "recent"
			? [...existing, ...(isLoggedIn ? assetTiles : [])]
			: assetTiles;

	// 每次打开回到 Recent、第一张
	useEffect(() => {
		if (open) {
			setTab("recent");
			setActiveIndex(0);
		}
	}, [open]);

	// 左栏是个滚动区，弹层放在里面会被裁（手机上 Prompt 上方只有 140 高的上传区，10/21 实测
	// Recent / Favorites 那一行被 tab 挡住）。所以挂到 body 上、fixed 定位：
	// 优先放上方，整屏也放不下时限高滚动；visualViewport 跟随手机键盘的可见区域。
	useLayoutEffect(() => {
		if (!open) {
			setPosition(null);
			return;
		}
		const update = () => {
			const anchor = anchorRef.current;
			const panel = panelRef.current;
			if (!anchor || !panel) {
				return;
			}
			const rect = anchor.getBoundingClientRect();
			const viewport = window.visualViewport;
			const viewportTop = viewport?.offsetTop ?? 0;
			const viewportLeft = viewport?.offsetLeft ?? 0;
			const viewportHeight = viewport?.height ?? window.innerHeight;
			const viewportWidth = viewport?.width ?? window.innerWidth;
			const maxHeight = viewportHeight - GAP * 2;
			const height = Math.min(panel.offsetHeight, maxHeight);
			const width = Math.min(rect.width, viewportWidth - GAP * 2);
			const spaceAbove = rect.top - viewportTop - GAP;
			const spaceBelow = viewportTop + viewportHeight - rect.bottom - GAP;
			const preferredTop =
				spaceAbove >= height + GAP || spaceAbove >= spaceBelow
					? rect.top - GAP - height
					: rect.bottom + GAP;
			const top = Math.max(
				viewportTop + GAP,
				Math.min(
					preferredTop,
					viewportTop + viewportHeight - height - GAP,
				),
			);
			const left = Math.max(
				viewportLeft + GAP,
				Math.min(rect.left, viewportLeft + viewportWidth - width - GAP),
			);
			setPosition({ left, top, width, maxHeight });
		};
		update();
		const observer = new ResizeObserver(update);
		if (panelRef.current) {
			observer.observe(panelRef.current);
		}
		window.addEventListener("resize", update);
		window.addEventListener("scroll", update, true);
		const viewport = window.visualViewport;
		viewport?.addEventListener("resize", update);
		viewport?.addEventListener("scroll", update);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", update);
			window.removeEventListener("scroll", update, true);
			viewport?.removeEventListener("resize", update);
			viewport?.removeEventListener("scroll", update);
		};
	}, [open, anchorRef]);

	// 限高后方向键选中的图片也要滚进可见区。
	useEffect(() => {
		if (open) {
			panelRef.current
				?.querySelectorAll("button[aria-disabled]")
				[activeIndex]?.scrollIntoView({ block: "nearest" });
		}
	}, [open, activeIndex, tab]);

	useEffect(() => {
		if (!open) {
			return;
		}
		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target as Node;
			if (
				panelRef.current?.contains(target) ||
				ignoreRef.current?.contains(target)
			) {
				return;
			}
			onClose();
		};
		document.addEventListener("pointerdown", handlePointerDown);
		return () =>
			document.removeEventListener("pointerdown", handlePointerDown);
	}, [open, onClose, ignoreRef]);

	const pick = (tile: Tile) => {
		if (tile.disabled) {
			return;
		}
		if (tile.n !== null) {
			onPickExisting(tile.n);
		} else {
			onPickAsset(tile.url, tab);
		}
	};

	useImperativeHandle(handleRef, () => ({
		handleKeyDown: (event) => {
			if (!open) {
				return false;
			}
			const moves: Record<string, number> = {
				ArrowRight: 1,
				ArrowLeft: -1,
				ArrowDown: COLUMNS,
				ArrowUp: -COLUMNS,
			};
			if (event.key in moves) {
				if (tiles.length > 0) {
					setActiveIndex((index) =>
						Math.min(
							Math.max(index + moves[event.key], 0),
							tiles.length - 1,
						),
					);
				}
				return true;
			}
			if (event.key === "Enter") {
				const tile = tiles[activeIndex];
				if (tile) {
					pick(tile);
				} else {
					onClose();
				}
				return true;
			}
			if (event.key === "Escape") {
				onClose();
				return true;
			}
			return false;
		},
	}));

	if (!open) {
		return null;
	}

	// 点面板里的东西时别让输入框失焦：手机上不收键盘，电脑上光标还在原地
	const keepFocus = (event: MouseEvent) => event.preventDefault();

	return createPortal(
		<section
			ref={panelRef}
			aria-label={t("pickerLabel")}
			onMouseDown={keepFocus}
			// 先量高度再定位：没算出位置前藏起来，免得在左上角闪一下
			style={{
				left: position?.left ?? 0,
				top: position?.top ?? 0,
				width: position?.width,
				maxHeight: position?.maxHeight,
				visibility: position ? "visible" : "hidden",
			}}
			// 选图时压过 Sonner（z-index: 999999999），避免 Undo Toast 挡住标签；关闭后仍可 Undo。
			className="studio-theme fixed z-[1000000000] flex flex-col gap-1 overflow-y-auto overscroll-contain rounded-2xl border border-zinc-200 bg-white p-2 shadow-[0_16px_36px_rgba(10,10,8,0.16),0_2px_6px_rgba(10,10,8,0.06)] [&>*]:shrink-0"
		>
			{isLoggedIn && (
				<div className="flex items-center justify-between px-1 pt-0.5 pb-1.5">
					<div
						role="tablist"
						aria-label={t("myAssets")}
						className="flex gap-0.5 rounded-[10px] bg-zinc-100 p-[3px]"
					>
						{(["recent", "favorite"] as const).map((value) => (
							<button
								key={value}
								type="button"
								role="tab"
								aria-selected={tab === value}
								onClick={() => {
									setTab(value);
									setActiveIndex(0);
								}}
								className={cn(
									"h-7 rounded-lg px-3 text-xs font-medium transition-colors",
									tab === value
										? "bg-white text-foreground shadow-[0_1px_2px_rgba(10,10,8,0.08)]"
										: "text-muted-foreground hover:text-foreground",
								)}
							>
								{t(value === "recent" ? "recent" : "favorites")}
							</button>
						))}
					</div>
					<span className="text-[11.5px] text-muted-foreground">
						{t("myAssets")}
					</span>
				</div>
			)}

			{tiles.length > 0 && (
				<div className="grid grid-cols-4 gap-2 px-1 pb-1.5">
					{tiles.map((tile, index) => (
						<button
							key={tile.key}
							type="button"
							title={tile.title}
							aria-disabled={tile.disabled}
							aria-label={
								tile.n !== null
									? t("insertExisting", { n: tile.n })
									: tile.disabled
										? t("full", { max: maxImages })
										: t("addAsNew", {
												n: inputImages.length + 1,
											})
							}
							onClick={() => pick(tile)}
							onMouseEnter={() => setActiveIndex(index)}
							className={cn(
								"relative aspect-square overflow-hidden rounded-[10px] bg-zinc-100",
								tile.n !== null &&
									"outline-2 -outline-offset-2 outline-primary",
								index === activeIndex &&
									!tile.disabled &&
									"ring-2 ring-zinc-900 ring-offset-2",
								tile.disabled &&
									"cursor-not-allowed opacity-40",
							)}
						>
							<img
								src={tile.url}
								alt=""
								loading="lazy"
								className="h-full w-full object-cover"
							/>
							{tile.n !== null && (
								<span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary/90 to-transparent px-1.5 pt-3 pb-1 text-left text-[10px] font-semibold text-white">
									{formatImageRef(tile.n)}
								</span>
							)}
						</button>
					))}
				</div>
			)}

			{isLoggedIn && isLoading && assetTiles.length === 0 && (
				<div className="grid grid-cols-4 gap-2 px-1 pb-1.5">
					{Array.from({ length: COLUMNS }, (_, index) => (
						<div
							key={index}
							className="aspect-square animate-pulse rounded-[10px] bg-zinc-100"
						/>
					))}
				</div>
			)}

			{isLoggedIn && (full || (!isLoading && tiles.length === 0)) && (
				<p className="px-1.5 pb-1 text-[11.5px] leading-relaxed text-muted-foreground">
					{full ? t("full", { max: maxImages }) : t("empty")}
				</p>
			)}

			{isLoggedIn && (
				<>
					<div className="mx-1 h-px bg-zinc-100" />
					<button
						type="button"
						onClick={onBrowseAll}
						className="flex h-10 items-center gap-2.5 rounded-[10px] px-2 text-[13px] text-zinc-700 hover:bg-zinc-50"
					>
						<FolderIcon className="size-4" />
						<span className="flex-1 text-left">
							{t("browseAll")}
						</span>
						<ChevronRightIcon className="size-4 text-zinc-400" />
					</button>
					<p className="px-2 pb-0.5 text-[11px] text-zinc-500 [@media(hover:none)]:hidden">
						{t("keyboardHint")}
					</p>
				</>
			)}
		</section>,
		document.body,
	);
}
